import { formatNumericInput } from "@/lib/utils";
import { Input } from "@heroui/react";
import { useTranslation } from "react-i18next";
import {
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

type InputFieldProps<T extends FieldValues> = {
  name: string;
  label: string;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  input_type?: "text" | "number";
  allowZero?: boolean;
  allowNegative?: boolean;
};

export function InputField<T extends FieldValues>({
  name,
  label,
  watch,
  setValue,
  register,
  input_type = "text",
  allowZero = true,
  allowNegative = true,
}: InputFieldProps<T>) {
  const { t } = useTranslation();
  const translatedLabel = t(label, { defaultValue: label });
  return (
    <Input
      isClearable
      key={name}
      type={"text"}
      onInput={
        input_type === "number"
          ? (e) => {
              const input = e.target as HTMLInputElement;
              input.value = formatNumericInput(
                input.value,
                allowZero,
                allowNegative,
              );
            }
          : undefined
      }
      label={translatedLabel}
      size="sm"
      labelPlacement="outside"
      value={watch(name as Path<T>) || ""}
      onClear={() => setValue(name as Path<T>, "" as PathValue<T, Path<T>>)}
      {...register(name as Path<T>)}
      classNames={{
        inputWrapper:
          "bg-white hover:bg-white hover:border-slate-400 data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:border-primary border-2 shadow-sm",
        label: "text-blue-600 group-data-[filled-within=true]:text-blue-600",
      }}
    />
  );
}
